import type { Observable } from 'rxjs';

export enum NbaType {
  Primary = 'primary',
  Secondary = 'secondary'
}

export interface NbaConditionConfig {
  type: string;
  params?: string[];
}

export interface PrimaryNbaReplacementConfig {
  type: string; // which is a type of an nba condition resolver
  config: PrimaryNbaConfig;
}

export interface PrimaryNbaDisplacementConfig {
  type: string; // which is a type of an nba condition resolver
  config: PrimaryNbaConfig;
}

// Contains properties which has to be configured in (currently) CARL
export interface NbaConfig extends NbaAction {
  id: string;
  label: string;
  icon?: string;
  customWidget?: string; // for external web components like wishlist save button
  savedToAccountLabel?: string; // for saved state on wishlist save button
  footnoteHints?: string[];
  trackingLinkName: string;
  conditions?: NbaConditionConfig[];
}

export interface OptionallyPrimaryNbaConfigAttachment {
  // Attached config is the "primary NBA" "secondary button" - next to the default button
  attachedConfig?: PrimaryNbaConfig;
}

export interface PrimaryNbaConfig extends NbaConfig {
  // we (currently) don't have a condition here because we _always_ have a primary NBA

  // ...but what we have is an _optional_ replacement (currently one) instead
  replacement?: PrimaryNbaReplacementConfig;

  // ...and we have this optional displacement (two buttons instead of one, default blue button becomes secondary button if condition apply)
  displacement?: PrimaryNbaDisplacementConfig;

  // an AB test could be performed, for version B this alternative primary NBA configuration is applied
  versionB?: Omit<PrimaryNbaConfig, 'versionB'>;
}

export interface PermanentSaveButtonConfig {
  active: boolean;
}

export type PermanentNbaConfig = NbaConfig[];

// Note: a secondary NBA can be a NBA but also can have further sub NBAs
export interface SecondaryNbaConfig extends NbaConfig {
  subNbas?: NbaConfig[];
}

export interface NbaConfigurations {
  permanentSaveButton: PermanentSaveButtonConfig;
  primaryNba: PrimaryNbaConfig; // always one primary NBA
  secondaryNbas?: SecondaryNbaConfig[];
  permanentNbas: PermanentNbaConfig;
}

export interface NbaConfigurationsWithAbTesting extends NbaConfigurations {
  secondaryNbasWithAbTesting?: Record<'common' | 'versionA' | 'versionB', SecondaryNbaConfig[]>;
}

export interface NbaAction {
  type: NbaActionType;
  data?: NbaLinkData | NbaModalData | NbaScrollToAnchorData;
}

export enum NbaActionType {
  None = 'none', // if e.g. a secondary nba is a button (without action) with options
  Modal = 'modal',
  ScrollToAnchor = 'scrollToAnchor', // uses viewportScroller to scroll to an anchor on the current page
  Link = 'link',
  Print = 'print',
  SaveToAccount = 'saveToAccount', // seamless save button widget
  NavigateToAnchor = 'navigateToAnchor', // uses router and fragment to navigate to a different page and anchor
  OpenFinancialCalculator = 'openFinancialCalculator', // opens the finance calculator
  MonthlyRateHide = 'monthlyRateHide' // hides the monthly rate
}

export interface NbaLinkData {
  link: string; // which can contain placeholders which get replaced by resolvers (e.g. {onlineCode})
}

export interface NbaModalData {
  component: string;
}

export interface NbaScrollToAnchorData {
  anchor: string;
}

export interface Nbas {
  primaryNba$: Observable<PrimaryNba>;
  secondaryNbas$: Observable<SecondaryNba[]>;
}

export interface Nba {
  href$: Observable<string>;
  htmlElementType: 'link' | 'button';
  trackingPosition: string;
}

export type PrimaryNba = PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment & Nba;

export interface SecondaryNba extends SecondaryNbaConfig, Nba {
  subNbas$?: Observable<SecondaryNba[]>;
  isActive: boolean;
}

export interface PermanentNba extends NbaConfig, Nba {
  isActive: boolean;
}

export type NbaPosition = 'top' | 'bottom' | 'summary-vehicle-data';
